import { EditOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  TreeSelect,
  DatePicker,
  message,
  Spin,
} from "antd";
import React, { useEffect, useState, useMemo } from "react";
import { store } from "../../../redux/configStore";
import { transformData } from "../../../utils/recursive";
import useStoreData from "../../../hooks/useStoreData";
import {
  updatePointPromotion,
  getByIdPointPromotion,
} from "../../../api/pointPromotion/pointPromotion";
import { useSelector } from "react-redux";
import { getListPointMechanisms } from "../../../api/pointMechanisms/pointMechanisms";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import ServiceProductSelector from "../../../components/common/ServiceProductSelector";
import { neutral, brand, spacing, radius, typography, border } from "../../../theme/tokens";
import { errorMessage } from "../../../lib/apiError";

const sectionStyle = {
  background: neutral[0],
  border: border.hairline,
  borderRadius: radius.md,
  padding: spacing[6],
};

const sectionTitle = {
  fontSize: typography.size.md,
  fontWeight: typography.weight.semibold,
  color: neutral[900],
  marginBottom: spacing[5],
  letterSpacing: "-0.005em",
};

export default function EditPointPromotionPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [promotionType, setPromotionType] = useState("PRODUCT");
  const [selectedIds, setSelectedIds] = useState([]);

  const { data: allListStore } = useStoreData();
  const { listPointMechanisms } = useSelector(
    (state) => state.persistedReducer.pointMechanismsSlice
  );

  const storeOptions = useMemo(() => {
    const data = transformData(allListStore?.data);
    return data?.filter((item) => item.parent_id === 0);
  }, [allListStore]);

  const initialProductItems = useMemo(() => {
    return (
      record?.items?.map((item) => ({
        id: item.service_product_id,
        name: item.service_product?.name || `SP #${item.service_product_id}`,
        code: item.service_product?.code,
      })) || []
    );
  }, [record]);

  useEffect(() => {
    const loadRecord = async () => {
      try {
        const response = await getByIdPointPromotion({ id });
        if (response?.data) {
          const r = response.data;
          setRecord(r);
          setPromotionType(r.type);
          setBranchId(r.mcoupon_branch_id);

          const ids = r.items?.map((it) => it.service_product_id) || [];
          setSelectedIds(ids);

          form.setFieldsValue({
            mcoupon_branch_id: r.mcoupon_branch_id,
            point_mechanism_id: r.point_mechanism_id,
            name: r.name,
            type: r.type,
            start_date: dayjs(r.start_date),
            end_date: dayjs(r.end_date),
            products: ids,
          });
        }
      } catch (error) {
        message.error("Không thể tải dữ liệu chương trình");
        navigate("/point-promotion");
      } finally {
        setPageLoading(false);
      }
    };
    if (id) loadRecord();
  }, [id, form, navigate]);

  useEffect(() => {
    if (branchId) {
      store.dispatch(getListPointMechanisms({ currentPage: 1, pageSize: 50, branchId }));
    }
  }, [branchId]);

  const handleProductsChange = (ids) => {
    setSelectedIds(ids);
    form.setFieldsValue({ products: ids });
  };

  const handleFormSubmit = async (values) => {
    setIsLoading(true);
    const payload = {
      name: values.name,
      start_date: values.start_date.format("YYYY-MM-DD HH:mm:ss"),
      end_date: values.end_date.format("YYYY-MM-DD HH:mm:ss"),
      products: selectedIds,
    };
    try {
      await updatePointPromotion({ id: record.id, data: payload });
      message.success("Cập nhật chương trình thành công!");
      navigate("/point-promotion");
    } catch (error) {
      message.error(errorMessage(error, "Cập nhật chương trình thất bại"));
    } finally {
      setIsLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: spacing[6], background: neutral[100], minHeight: "100vh" }}>
      <div style={{ width: "100%" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[5] }}>
          <EditOutlined style={{ color: brand.primary, fontSize: 18 }} />
          <h2 style={{ margin: 0, fontSize: typography.size.xl, fontWeight: typography.weight.semibold, color: neutral[900], letterSpacing: "-0.01em" }}>
            Chỉnh sửa chương trình tích điểm
          </h2>
        </div>

        <Form form={form} layout="vertical" requiredMark={false} onFinish={handleFormSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[5] }}>
            {/* Section 1 - Thông tin cơ bản */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>Thông tin cơ bản</div>
              <Row gutter={[spacing[4], spacing[4]]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Đơn vị"
                    name="mcoupon_branch_id"
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: "Vui lòng chọn đơn vị!" }]}
                  >
                    <TreeSelect
                      allowClear
                      treeData={storeOptions}
                      placeholder="Chọn đơn vị..."
                      filterTreeNode={(input, node) => node.title.toLowerCase().includes(input.toLowerCase())}
                      style={{ width: "100%" }}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Cơ chế tích điểm"
                    name="point_mechanism_id"
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: "Vui lòng chọn cơ chế!" }]}
                  >
                    <Select
                      placeholder="Chọn cơ chế..."
                      options={(listPointMechanisms || []).map((t) => ({ label: t.name, value: t.id }))}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    label="Tên chương trình"
                    name="name"
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: "Vui lòng nhập tên chương trình!" }]}
                  >
                    <Input placeholder="VD: Chương trình tích điểm tháng 6" allowClear />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Section 2 - Loại chương trình */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>Loại chương trình</div>
              <Row gutter={[spacing[4], spacing[4]]}>
                <Col xs={24} md={12}>
                  <Form.Item label="Phạm vi áp dụng" name="type" style={{ marginBottom: 0 }} rules={[{ required: true }]}>
                    <Select disabled onChange={setPromotionType}>
                      <Select.Option value="PRODUCT">Sản phẩm</Select.Option>
                      <Select.Option value="OTHER">Khác</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                {/* Ẩn "Kiểu áp dụng đồng thời" (promotion_type) — chưa dùng; update cũng không gửi field này.
                <Col xs={24} md={12}>
                  <Form.Item label="Kiểu áp dụng đồng thời" name="promotion_type" style={{ marginBottom: 0 }} rules={[{ required: true }]}>
                    <Select disabled>
                      <Select.Option value="CHOOSABLE">Tùy chọn</Select.Option>
                      <Select.Option value="DEFAULT">Mặc định</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                */}
              </Row>
            </div>

            {/* Section 3 - Thời gian */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>Thời gian chương trình</div>
              <Row gutter={[spacing[4], spacing[4]]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Bắt đầu"
                    name="start_date"
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: "Vui lòng chọn ngày bắt đầu!" }]}
                  >
                    <DatePicker showTime style={{ width: "100%" }} format="DD/MM/YYYY HH:mm" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Kết thúc"
                    name="end_date"
                    style={{ marginBottom: 0 }}
                    rules={[
                      { required: true, message: "Vui lòng chọn ngày kết thúc!" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || !getFieldValue("start_date") || value.isAfter(getFieldValue("start_date"))) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Ngày kết thúc phải sau ngày bắt đầu!"));
                        },
                      }),
                    ]}
                  >
                    <DatePicker showTime style={{ width: "100%" }} format="DD/MM/YYYY HH:mm" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Section 4 - Sản phẩm */}
            {promotionType === "PRODUCT" && (
              <div style={sectionStyle}>
                <div style={sectionTitle}>
                  Danh sách sản phẩm{" "}
                  <span style={{ fontSize: typography.size.sm, fontWeight: typography.weight.regular, color: neutral[500] }}>
                    ({selectedIds.length} đã chọn)
                  </span>
                </div>
                <Form.Item
                  name="products"
                  style={{ marginBottom: 0 }}
                  rules={[{ required: true, message: "Vui lòng chọn sản phẩm!" }]}
                >
                  <ServiceProductSelector
                    branchId={branchId}
                    value={selectedIds}
                    initialItems={initialProductItems}
                    onChange={handleProductsChange}
                  />
                </Form.Item>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[3] }}>
              <Button onClick={() => navigate("/point-promotion")} style={{ minWidth: 120 }}>
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<EditOutlined />}
                loading={isLoading}
                style={{ minWidth: 140 }}
              >
                Cập nhật
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
}
